const CHART_TYPE_MAP = {
    fenix_line_chart: 'line',
    fenix_stacked_area_chart: 'area',
    fenix_column_chart: 'column',
    fenix_bar_chart: 'bar',
    fenix_polar_chart: 'polar',
    fenix_mixed_chart: 'mixed',
    fenix_tree_map_chart: 'treemap',
    fenix_heat_map_chart: 'heatmap',
    fenix_timeline_chart: 'timeline',
};

const camelize = (value) =>
    value.replace(/_([a-z])/g, (_, character) => character.toUpperCase());

const unwrapField = (value) => value?.field || value || '';

const adaptFilters = (filters = []) =>
    filters.map(({ field, operator = 'eq', value }) => ({
        key: operator === 'ne' ? `${field}__ne` : field,
        value,
    }));

const adaptAnnotations = (annotations = []) =>
    annotations.map(({ value }) => ({
        showOnlyOnCountries: value.show_only_on_countries || [],
        annotationItems: (value.annotation_items || []).map((item) => ({
            xAxisCoord: item.x_axis_coord,
            xAxisRelativeAlignment: item.x_axis_relative_alignment,
            yAxisCoord: item.y_axis_coord,
            yAxisRelativeAlignment: item.y_axis_relative_alignment,
            text: item.text,
        })),
    }));

const buildStandardPlotOptions = (chartType, chartBlock) => {
    if (chartType === 'line') {
        return {
            series: {
                marker: { enabled: chartBlock.enableLineSymbols ?? true },
            },
        };
    }
    if (chartType === 'area') {
        return {
            series: {
                marker: { enabled: chartBlock.enableLineSymbols ?? true },
            },
            area: {
                stacking: chartBlock.stackingType ?? 'normal',
                lineWidth: 1,
                marker: { lineWidth: 1 },
            },
        };
    }
    if (chartType === 'polar') {
        // Mirror PolarChartBlock's published plotOptions, which carries the
        // line-symbol marker alongside stacking and disabled data labels.
        return {
            series: {
                stacking: chartBlock.yAxisStackValues ?? false,
                dataLabels: { enabled: false },
                marker: { enabled: chartBlock.enableLineSymbols ?? true },
            },
        };
    }
    return {
        series: {
            stacking: chartBlock.yAxisStackValues ?? false,
            dataLabels: { enabled: false },
        },
    };
};

export const chartBlockFromExport = ({ type, value = {} }) => {
    const chartType = CHART_TYPE_MAP[type];
    if (!chartType) return null;
    const chartBlock = Object.fromEntries(
        Object.entries(value)
            .filter(([key]) => key !== 'series')
            .map(([key, item]) => [camelize(key), item])
    );
    // Mixed charts publish as chartType 'line' with an isMixedChart flag (see
    // MixedChartBlock), letting each series pick its own type via
    // seriesChartType. Emit the same shape here so the preview hands the
    // renderer a chart block identical to the published page's.
    const isMixed = chartType === 'mixed';
    const resolvedChartType = isMixed ? 'line' : chartType;
    chartBlock.chartType = resolvedChartType;
    if (isMixed) {
        chartBlock.isMixedChart = true;
    }
    if (chartType === 'treemap') {
        chartBlock.plotOptions = {
            treemap: {
                dataLabels: {
                    style: { textOutline: 'none', fontSize: '14px' },
                },
            },
        };
    } else if (chartType !== 'heatmap' && chartType !== 'timeline') {
        chartBlock.plotOptions = buildStandardPlotOptions(
            resolvedChartType,
            chartBlock
        );
    }
    chartBlock.propertySeriesQueries = (value.series || [])
        .filter(({ type: seriesType }) => seriesType === 'property_series')
        .map(({ value: series }) => ({
            dataSource: {
                type: series.data_source?.[0]?.type,
                value: series.data_source?.[0]?.value?.dataset,
            },
            filterParams: adaptFilters(series.filter_query?.filters),
            serie: {
                property: unwrapField(series.serie?.property),
                label: series.serie?.label || '',
                color: series.serie?.color,
                negateSerieValues: !!series.serie?.negate_serie_values,
                xAxisCategoryProperty: unwrapField(
                    series.serie?.x_axis_category_property
                ),
                yAxisCategoryProperty: unwrapField(
                    series.serie?.y_axis_category_property
                ),
                zone: series.serie?.zone?.axis
                    ? {
                          axis: series.serie.zone.axis,
                          startValue: series.serie.zone.start_value,
                          endValue: series.serie.zone.end_value,
                      }
                    : null,
                ...(chartType === 'timeline'
                    ? {
                          dateProperty: unwrapField(
                              series.serie?.date_property
                          ),
                          titleProperty: unwrapField(
                              series.serie?.title_property
                          ),
                          descriptionProperty: unwrapField(
                              series.serie?.description_property
                          ),
                      }
                    : {}),
            },
            ...(chartType === 'treemap'
                ? {
                      hierarchyLevels: (series.hierarchy_levels || []).map(
                          (level) => ({
                              groupingProperty: level.ontology_property,
                              labelProperty: level.label_property,
                              useForColoring: level.use_for_coloring,
                          })
                      ),
                  }
                : {}),
            ...(series.series_chart_type
                ? { seriesChartType: series.series_chart_type }
                : {}),
            annotations: adaptAnnotations(series.annotations),
        }));
    return chartBlock;
};
